import React, { useState, ReactElement, useEffect } from "react";
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { QRCodeSVG } from 'qrcode.react';
import * as types from '../../../constants/actionTypes';
import Notification from '../../Common/Notification';
import './TfaSetupWorkflowStep.scss';
import { Redirect } from 'react-router-dom';
import Routes from '../../../constants/routes';
import { WorkflowStepNames } from '../../../constants/constants';
import { InsertSpaceEvery4Chars } from '../../../helpers/strings';
import { actions as accountActions } from '../../../actions/accountActions';
import { actions as mfaActions } from '../../../actions/mfaActions';

interface TfaSetupWorkflowStepProps {
    user: object;
    step: TfaWorkflowStep;
}

interface TfaWorkflowStep {
    name: string;
    sharedKey: string;
    authenticatorUri: string;
}

const TfaSetupWorkflowStep = ({ user, step }: TfaSetupWorkflowStepProps): ReactElement => {

    const [isCanceled, setIsCanceled] = useState<boolean>(false);
    const [code, setCode] = useState<string>('');

    const dispatch: Dispatch = useDispatch();
    const verify2faConfig = async (authCode) => dispatch(mfaActions.verifyAuthenticator(authCode));
    const completeWorkflowStep = async (stepName) => dispatch(accountActions.completeWorkflowStep(stepName));

    useEffect(() => {
        if (!step || step.name !== WorkflowStepNames.ConfigureTfa) {
            Notification("error", "Error", "Invalid Step Name: Expected " + WorkflowStepNames.ConfigureTfa + " Actual: " + step.name);
            setIsCanceled(true);
        }
    }, []);

    const onContinueClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        try {
            const response = await verify2faConfig(code.replace(/ /g, ''));
            if (response.type === types.MFA_VERIFY_AUTHENTICATOR_SUCCESS) {
                // Don't complete the workflow step until the save is successful
                completeWorkflowStep(step.name);
            } else if (response.type === types.MFA_VERIFY_AUTHENTICATOR_FAILURE) {
                Notification("error", "Error", "Problem saving authenticator configuration.");
            }
        } catch (error) {
            Notification("error", "An Error Occurred", error);
        }
    }

    const onCancelClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsCanceled(true);
    }

    return (
        isCanceled ?
            <Redirect to={Routes.LoginPage} />
            :
            <form onSubmit={onContinueClick} noValidate className="tfa-workflow-step">
                <p>Your administrator has made two factor authentication (2FA) required for your account.</p>
                <p>Scan the QR code or enter the following key into your two factor authenticator app.  Spaces and casing do not matter.</p>
                <p>Pacer recommends <a href="https://support.microsoft.com/en-us/account-billing/download-microsoft-authenticator-351498fc-850a-45da-b7b6-27e523b8702a">Microsoft Authenticator</a></p>

                <div className="qr-container">
                    <QRCodeSVG
                        value={step.authenticatorUri}
                        title={"Scan with your authenticator app on your mobile device"}
                        size={128}
                        bgColor={"#ffffff"}
                        fgColor={"#000000"}
                        level={"L"}
                        marginSize={2}
                    />
                </div>

                <div className="auth-key">{InsertSpaceEvery4Chars(step.sharedKey)}</div>

                <div className="row justify-content-center">
                    <div className="col-6">
                        <input className='form-control mb-4'
                            id='txtCode'
                            name='txtCode'
                            type='text'
                            placeholder='Enter code'
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            autoComplete="off"
                            maxLength={8}
                        />
                    </div>
                </div>

                <div className="buttons row">
                    <div className="col-6">
                        <button className="btn btn-outline-light" onClick={onCancelClick}>Cancel</button>
                    </div>
                    <div className="col-6">
                        <button className="btn btn-outline-primary" onClick={onContinueClick}>Continue</button>
                    </div>
                </div>
            </form>
    );
}

export default TfaSetupWorkflowStep;